import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import type { BridgeState } from "@loom/shared";

import { useT, usePlural, type MessageKey } from "../i18n/index.js";
import { reconnectBody } from "../lib/reconnect-body";
import {
  useSessionPagehideBeacon,
  useSessionBridgeEnsure,
} from "../lib/sessionBeacon";
import { RemountSafeStoreProvider } from "../state/remount-safe-state";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { FIELD } from "../ui/classes";
import { Spinner } from "../ui/Spinner";
import { HistorySidebar } from "../sessions/HistorySidebar";
import { ReauthBanner } from "../auth/ReauthBanner";
import { SandboxStatus } from "../components/SandboxStatus";
import { IdleReconnectDialog } from "./IdleReconnectDialog";

import { isSessionDead } from "./session-status";
import {
  useWsSession,
  type BridgeStatusState,
  type SessionMode,
  type UseWsSession,
} from "./useWsSession";
import { RenderEntry } from "./render";
import { composeChatBatch } from "./render/chatAnswer";
import { unreadUserTurns, useRedOfferLatch } from "./render/loomAnswer";
import { useAnswerHold, type HeldBatch } from "./render/useAnswerHold";
import { composeBatch } from "../focus/answer-frame";
import { useSessionProjectId } from "../projects/useSessions";

interface BridgeBadgeProps {
  readonly bridge: BridgeStatusState;
  readonly onReconnect: () => void;
  readonly reconnecting: boolean;
}

const bridgeBadgeKey = (state: BridgeState): MessageKey => {
  switch (state) {
    case "pending":
      return "chatTop.bridge.pending";
    case "cloning":
      return "chatTop.bridge.cloning";
    case "ready":
      return "chatTop.bridge.ready";
    case "terminated":
      return "chatTop.bridge.terminated";
    case "error":
      return "chatTop.bridge.error";
  }
};

const bridgeBadgeTone = (state: BridgeState): string => {
  switch (state) {
    case "pending":
    case "cloning":
      return "text-caution-fg";
    case "ready":
      return "text-success-fg";
    case "terminated":
    case "error":
      return "text-danger-fg";
  }
};

const BridgeBadge = ({ bridge, onReconnect, reconnecting }: BridgeBadgeProps) => {
  const t = useT();
  const tone = bridgeBadgeTone(bridge.state);
  const text = bridge.message ?? t(bridgeBadgeKey(bridge.state));
  // Phase 8 — a `terminated` bridge is recoverable. Surface a Resume
  // action that triggers a fresh CI job with `LOOM_RESUME=true`; the
  // server re-hydrates the JSONL and the next prompt continues the
  // prior conversation.
  const resumable = bridge.state === "terminated" || bridge.state === "error";
  return (
    <div
      className={`flex items-center gap-1 text-xs ${tone}`}
      aria-live="polite"
      data-testid="bridge-status-badge"
      data-state={bridge.state}
    >
      {(bridge.state === "pending" || bridge.state === "cloning") && (
        <Spinner label="" />
      )}
      <span>{text}</span>
      {resumable && (
        <button
          type="button"
          onClick={onReconnect}
          disabled={reconnecting}
          className="ml-2 rounded border border-current px-1.5 py-0.5 text-xs font-medium hover:bg-danger-soft disabled:opacity-50"
          data-testid="bridge-resume-button"
        >
          {reconnecting ? t("chatTop.resuming") : t("chatTop.resume")}
        </button>
      )}
    </div>
  );
};

interface ModeToggleProps {
  readonly mode: SessionMode;
  readonly bridgeReady: boolean;
  readonly onSelect: (next: SessionMode) => void;
}

const ModeToggle = ({ mode, bridgeReady, onSelect }: ModeToggleProps) => {
  const t = useT();
  const disabledTitle = bridgeReady ? undefined : t("chatTop.codingLocked");
  const buttonClass = (active: boolean): string =>
    [
      "px-2 py-1 text-xs font-medium transition-colors",
      active
        ? "bg-primary text-on-solid"
        : "bg-surface text-ink-muted hover:bg-surface-3",
    ].join(" ");
  return (
    <div
      role="group"
      aria-label={t("chatTop.modeGroupLabel")}
      data-testid="mode-toggle"
      data-mode={mode}
      className="inline-flex overflow-hidden rounded border border-line-strong"
    >
      <button
        type="button"
        className={buttonClass(mode === "pm")}
        onClick={() => onSelect("pm")}
        aria-pressed={mode === "pm"}
      >
        {t("chatTop.modePm")}
      </button>
      <button
        type="button"
        className={buttonClass(mode === "coding")}
        onClick={() => onSelect("coding")}
        aria-pressed={mode === "coding"}
        disabled={!bridgeReady && mode !== "coding"}
        title={disabledTitle}
      >
        {t("chatTop.modeCoding")}
      </button>
    </div>
  );
};

interface ChatPageProps {
  readonly sessionId: string;
  readonly onPickSession: (id: string) => void;
  readonly onBackToPicker: () => void;
  /**
   * Phase 9 — projects route renders its own `SessionSidebar` outside
   * ChatPage, so the legacy `HistorySidebar` here is suppressed. The
   * default (no flag) preserves the pre-Phase-9 sidebar for the legacy
   * `/chat/:sessionId` route.
   */
  readonly hideHistorySidebar?: boolean;
  /**
   * Phase 3a — the dashboard's `ChatDrawer` wraps ChatPage as a slide-in
   * panel. `embedded` swaps `min-h-screen` for `h-full` so the chat fills
   * the drawer instead of the viewport, hides the back-to-picker button,
   * and implies `hideHistorySidebar`.
   */
  readonly embedded?: boolean;
  /**
   * Phase 3a — seed the draft input. When non-null, the draft is set to
   * `seed` on mount + whenever the seed changes; the parent clears it via
   * `onSeedConsumed` so a stale seed doesn't override a later edit.
   */
  readonly seed?: string | null;
  /**
   * The byte-exact span of `seed` the USER authored, for a MACHINE-COMPOSED seed
   * (`seedMode: "send"`). The grounding gate then quotes only that span, so the
   * boilerplate the surface wrapped around it cannot come back as certified user
   * evidence. `""` says "nothing here is quotable" and is a real value;
   * `undefined` declares no span and leaves the gate scanning the whole seed.
   * Ignored in `"draft"` mode — the user edits and sends that text themselves,
   * so it is a genuine human turn.
   */
  readonly seedEvidence?: string;
  readonly onSeedConsumed?: () => void;
  /**
   * "draft" (default) drops the seed into the textbox and waits for the
   * user to click Send. "send" auto-sends it as a user_message — used by
   * the post-create refinement flow where the seed is a system-shaped
   * "please refine issue #N" prompt that the user shouldn't have to
   * re-trigger. Either way `onSeedConsumed` fires immediately so the
   * parent can clear the prop.
   */
  readonly seedMode?: "draft" | "send";
  /**
   * When supplied, ChatPage renders from THIS session instead of opening its
   * own WebSocket. The IssuePage refine view owns the single live session (so
   * it can also pin the readiness card outside the scroll) and threads it in
   * here; ChatPage's own `useWsSession` is then called with `null` and
   * no-ops, keeping a single socket onto the session. The three other
   * call sites (`/chat/:id`, ProjectChatPage, ChatDrawer) omit it and keep
   * instantiating their own session.
   */
  readonly externalSession?: UseWsSession;
  /**
   * Hide the inline readiness card from the timeline. The IssuePage refine
   * view renders the card pinned in the editor pane instead, so the inline
   * copy would be a duplicate. Default (false) keeps the card inline for
   * every other chat surface.
   */
  readonly suppressReadinessCard?: boolean;
  /**
   * Project context for the inline readiness card's milestone dropdown. Only
   * IssuePage (the refine surface) threads it in. The other call sites omit it:
   * their card has no list to pick a milestone from and says so — never a text
   * box — and a green milestone on it is checked against the session's own
   * project, read when one shows (`useSessionProjectId`).
   */
  readonly projectId?: number;
  /**
   * Flow identity (Phase 1 — flow boundaries). When set, this chat surface is
   * bound to an issue's refine flow: the auto-sent seed opens the flow
   * (`boundary: true`) and every subsequent turn carries `{ issueIid }` so the
   * server keeps scoping the readiness-card evidence to THIS issue. Only the
   * IssuePage refine view threads it in; general-chat call sites (`/chat/:id`,
   * ProjectChatPage, ChatDrawer) omit it and their turns carry no flow.
   */
  readonly flowIssueIid?: number;
  /**
   * Offered every composer message before it is sent. Returning `true` means
   * the host took it: the issue page sends a rail message WITH its held
   * answers, in one frame, so the rail cannot kill the turn those answers are
   * about to start.
   */
  readonly beforeSend?: (text: string) => boolean;
}

export function ChatPage({
  sessionId,
  onPickSession,
  onBackToPicker,
  hideHistorySidebar = false,
  embedded = false,
  seed = null,
  seedEvidence,
  onSeedConsumed,
  seedMode = "draft",
  externalSession,
  suppressReadinessCard = false,
  projectId,
  flowIssueIid,
  beforeSend,
}: ChatPageProps) {
  const t = useT();
  const tn = usePlural();
  // When an externalSession is threaded in (IssuePage refine view), call the
  // hook with `null` so it no-ops — the single live socket is the owner's.
  // Otherwise this is the owner and opens its own socket.
  const ownSession = useWsSession(externalSession ? null : sessionId);
  const { timeline, status, reauth, bridge, sandbox, mode, branch, send, sendSystem, clearReauth, reconnect, resetSession, idleCountdownEndsAt, dismissIdle, uiState, applyResult, agentBusy, busyKnown, ackedIds, resetEpoch } =
    externalSession ?? ownSession;
  // A refused apply. `applyResult` is a single "last apply" slot, so the card
  // matches it on its own iid before rendering it.
  const applyFailure =
    applyResult !== null && !applyResult.ok && applyResult.error !== undefined
      ? { issueIid: applyResult.issueIid, error: applyResult.error }
      : undefined;
  // The composer draft survives this component unmounting (IssuePage unmounts
  // the embedded chat on every side-tab switch and every detail-refetch null
  // window) by riding the session's remount-safe store. Reconnects don't
  // unmount ChatPage itself, but the embedded mounts do — same store, both
  // covered. Read/written directly (not via the hook) because the provider
  // boundary is below this component.
  const [draft, setDraftState] = useState(() => {
    const persisted = uiState.get("chat:composerDraft");
    return typeof persisted === "string" ? persisted : "";
  });
  const setDraft = useCallback(
    (next: string) => {
      uiState.set("chat:composerDraft", next);
      setDraftState(next);
    },
    [uiState],
  );
  const visibleTimeline = suppressReadinessCard
    ? timeline.filter((entry) => entry.kind !== "readiness_card")
    : timeline;
  // A green milestone on an inline card is shown as Accept will treat it: apply
  // resolves the title against the project's list and leaves the issue's
  // milestone alone when nothing matches, so the row must say THAT rather than
  // show a value that will not land. That needs the project, which only
  // IssuePage hands in; on `/chat/:id`, the project chat and the dashboard
  // drawer it is read off the session — and only once a card shows a green
  // milestone, so every other chat costs no request.
  const showsGreenMilestone = useMemo(
    () =>
      projectId === undefined &&
      !suppressReadinessCard &&
      timeline.some(
        (e) =>
          e.kind === "readiness_card" &&
          e.verdicts.some((v) => v.field === "milestone" && v.verdict === "green"),
      ),
    [projectId, suppressReadinessCard, timeline],
  );
  const milestoneListProjectId = useSessionProjectId(
    showsGreenMilestone ? sessionId : undefined,
  );
  const sidebarHidden = hideHistorySidebar || embedded;

  // Flow tagging (Phase 1 — flow boundaries). A bound chat surface stamps every
  // turn with `{ issueIid }` so the server keeps the readiness-card evidence
  // scoped to this issue; the seed additionally opens the flow with
  // `boundary: true`. Undefined on general-chat surfaces (no issue identity).
  const turnFlow =
    flowIssueIid !== undefined ? { flow: { issueIid: flowIssueIid } } : undefined;
  // The inline readiness card's HELD answers (a click on Loom's answer, or a
  // typed Send on a row). They go out as ONE message, on the lane a typed chat
  // message takes (`turnFlow`, unbound in a plain chat): never a
  // `question_answer`, which would adopt the chat for the issue, and never with
  // a `flow` of their own, which would be a flow boundary and drop the agent's
  // conversation. No click kills a running turn: the answers wait while the
  // agent works. Keyed `<iid>:<field>`, since one chat can carry cards for two
  // issues.
  //
  // In a chat no issue OWNS, each story answer goes out under its name line
  // ("Role:", "I want:", "So that:" — `composeChatBatch`), the answers wait
  // until every open row of the latest card is answered or the user has been
  // quiet for a while, and a composer message carries them. A chat an issue
  // owns (a bound turn in it — every echo then carries an `issueIid`) is routed
  // under the REFINE directive, which tells the model to keep a labelled token
  // verbatim, so a name line would be quoted into the value ("As Role: …"):
  // there the answers go out bare, one message as soon as the agent is idle.
  const latestCard = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i -= 1) {
      const e = timeline[i];
      if (e?.kind === "readiness_card") return e;
    }
    return undefined;
  }, [timeline]);
  // What each batch THIS mount sent carried, by its delivery id (see `settled`).
  const ownFlushesRef = useRef<Map<string, ReadonlySet<string>>>(new Map());
  const latestCardStamp = latestCard?.groundedThroughSeq;
  const issueOwned =
    flowIssueIid !== undefined ||
    timeline.some((e) => e.kind === "user_message" && typeof e.issueIid === "number");
  const openCardKeys =
    issueOwned || latestCard === undefined
      ? []
      : latestCard.verdicts
          .filter(
            (v) =>
              (v.verdict === "needs_input" || v.verdict === "red") && v.declined !== true,
          )
          .map((v) => `${String(latestCard.issueIid)}:${v.field}`);
  // A "New chat" armed on the composer rides the composer's message, held
  // answers included — read once by the flush that message triggers.
  const newChatFlushRef = useRef(false);
  const onFlushCard = (batch: HeldBatch): string | null => {
    const newChat = newChatFlushRef.current;
    newChatFlushRef.current = false;
    const text = issueOwned
      ? composeBatch(
          new Map(batch.entries.map((e) => [e.key, e.text])),
          batch.freeText,
        )
      : composeChatBatch(
          batch.entries.map((e) => ({ field: e.field ?? "", text: e.text })),
          batch.freeText,
        );
    if (text.trim() === "") return null;
    const id = send(text, newChat ? { newChat: true } : turnFlow);
    // A composer message riding the batch could say anything about any row, so
    // only a batch of answers alone is one this mount can vouch for.
    if (id !== "" && batch.freeText === undefined) {
      ownFlushesRef.current.set(id, new Set(batch.entries.map((e) => e.key)));
    }
    return id;
  };
  const cardHoldState = useAnswerHold({
    storageKey: null,
    sessionId,
    resetEpoch,
    canFlush:
      !suppressReadinessCard &&
      status === "open" &&
      // Idle only once the server has said so to this socket (see useWsSession).
      busyKnown &&
      // A plain chat waits for the agent to be idle, and for the user to be
      // done typing — a paragraph in the composer goes out WITH the answers.
      (issueOwned || (!agentBusy && draft.trim() === "")),
    agentBusy,
    openKeys: openCardKeys,
    // No card yet (a replay that has not reached it) is not "every row
    // answered" in a plain chat: the quiet timer decides then.
    openElsewhere: !issueOwned && latestCard === undefined,
    groundedThroughSeq: latestCardStamp,
    ackedIds,
    onFlush: onFlushCard,
  });
  const cardRedLatch = useRedOfferLatch();
  const cardHold = {
    onAnswer: (issueIid: number, field: string, text: string) =>
      cardHoldState.add({ key: `${issueIid}:${field}`, field, text }),
    onUndo: (issueIid: number, field: string) => cardHoldState.undo(`${issueIid}:${field}`),
    held: (issueIid: number) => {
      const m = new Map<string, string>();
      for (const e of cardHoldState.held.values()) {
        if (e.field !== null && e.key === `${issueIid}:${e.field}`) m.set(e.field, e.text);
      }
      return m;
    },
    sent: (issueIid: number) => {
      const out = new Set<string>();
      for (const k of cardHoldState.sent) {
        if (k.startsWith(`${issueIid}:`)) out.add(k.slice(`${issueIid}:`.length));
      }
      return out;
    },
    // A red row's value is Loom's wording, offered once SETTLED: an open socket
    // whose busy state the server has told it and that reads idle, nothing in
    // flight, and a card that has read every user turn that could have answered
    // this row. That last one is read off the TIMELINE (a reload rebuilds it):
    // any user turn newer than the card's stamp blocks the offer, except a batch
    // this mount sent that did not carry this row. Latched per (field, value).
    settled: (issueIid: number, field: string) => {
      const card = [...timeline]
        .reverse()
        .find((e) => e.kind === "readiness_card" && e.issueIid === issueIid);
      const v =
        card !== undefined && card.kind === "readiness_card"
          ? card.verdicts.find((x) => x.field === field)
          : undefined;
      const key = `${issueIid}:${field}`;
      // A plain chat's answers go out unbound, so every user turn counts.
      const blocked = unreadUserTurns(
        timeline,
        card !== undefined && card.kind === "readiness_card" ? card : null,
        null,
      ).some((turn) => {
        const own =
          turn.clientMsgId !== undefined ? ownFlushesRef.current.get(turn.clientMsgId) : undefined;
        return own === undefined || own.has(key);
      });
      let now =
        status === "open" &&
        busyKnown &&
        !agentBusy &&
        cardHoldState.sent.size === 0 &&
        !blocked;
      const answeredId = cardHoldState.answeredAt.get(`${issueIid}:${field}`);
      if (now && answeredId !== undefined) {
        const ack = answeredId !== "" ? (ackedIds.get(answeredId) ?? null) : null;
        const stamp =
          card !== undefined && card.kind === "readiness_card"
            ? card.groundedThroughSeq
            : undefined;
        now = ack !== null && stamp !== undefined && stamp >= ack;
      }
      return cardRedLatch(
        key,
        v?.verdict,
        typeof v?.value === "string" ? v.value : undefined,
        now,
        blocked,
      );
    },
  };

  // "New chat" (Phase 1 — flow boundaries) is offered only on a PURE-chat
  // surface (no bound issue). It arms a fresh CLI conversation on the next turn
  // (keeps the scrollback, resets the assistant's working context). A flow
  // surface expresses a fresh conversation via its boundary seed instead, so
  // the button is hidden there.
  const isPureChat = flowIssueIid === undefined;
  const [newChatArmed, setNewChatArmed] = useState(false);
  // Send a composer turn: a queued "New chat" rides this turn as `newChat`
  // (pure chat) and then disarms; otherwise the surface's flow tag (if any).
  const sendComposer = (text: string): void => {
    if (beforeSend?.(text) === true) {
      setDraft("");
      return;
    }
    // Held card answers ride this message: one turn, not one for them and a
    // second that kills it.
    newChatFlushRef.current = newChatArmed;
    if (cardHoldState.count === 0 || !cardHoldState.flushNow(text)) {
      newChatFlushRef.current = false;
      send(text, newChatArmed ? { newChat: true } : turnFlow);
    }
    setDraft("");
    if (newChatArmed) setNewChatArmed(false);
  };

  // Drawer seed:
  //   - "draft" (default) drops the seed into the textbox and waits for
  //     the user to click Send. Used by DecisionsInbox where the user
  //     may want to edit the prompt before sending.
  //   - "send" auto-fires the seed as a user_message. Used by the
  //     post-create refinement flow where the seed is a "please refine
  //     issue #N" prompt the user shouldn't have to re-trigger.
  // Send-mode is gated on `status === "open"`: openWsSession's send is
  // a no-op when the socket isn't OPEN, so firing the seed before the
  // WS handshake completes silently drops it. The effect re-runs when
  // status flips to "open" and sends then.
  useEffect(() => {
    if (seed === null || seed.length === 0) return;
    if (seedMode === "send") {
      if (status !== "open") return;
      // The seed opens the flow — `boundary: true` clears the prior flow's CLI
      // conversation server-side and moves the evidence eviction horizon. It is
      // also machine-composed, so it declares its user-authored span (spread on
      // `!== undefined`: `""` is the real answer for a surface whose seed the
      // user wrote none of, and dropping it as falsy re-opens the laundering).
      send(seed, {
        ...(flowIssueIid !== undefined
          ? { flow: { issueIid: flowIssueIid, boundary: true } }
          : {}),
        ...(seedEvidence !== undefined ? { evidenceText: seedEvidence } : {}),
      });
    } else {
      setDraft(seed);
    }
    onSeedConsumed?.();
  }, [seed, seedEvidence, seedMode, send, onSeedConsumed, status, flowIssueIid]);
  const [mrError, setMrError] = useState<string | null>(null);
  const [mrSubmitting, setMrSubmitting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onResetClick = async () => {
    if (resetting) return;
    const ok = window.confirm(t("chatTop.resetConfirm"));
    if (!ok) return;
    setResetting(true);
    try {
      await fetch(`/sessions/${sessionId}/reset`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      // Fresh WS attach + timeline clear, PLUS the reset-specific
      // invalidation: the server kept the sessionId but rebuilt the bus from
      // seq 0, so the remount-safe store and any seq-ordered latch must not
      // survive into the reborn conversation (resetSession clears/bumps them;
      // a plain reconnect() deliberately would not).
      resetSession();
    } finally {
      setResetting(false);
    }
  };

  const onReconnect = async () => {
    if (reconnecting) return;
    setReconnecting(true);
    try {
      const res = await fetch(`/sessions/${sessionId}/reconnect`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: reconnectBody(),
      });
      if (!res.ok) {
        // Best-effort — the WS subscription will pick up the new
        // `bridge_status` events as the freshly triggered pipeline cold
        // starts. On failure, surface nothing extra here; the badge
        // already shows the terminated state.
      }
    } finally {
      setReconnecting(false);
    }
  };

  const onModeSelect = (next: SessionMode) => {
    if (next === mode) return;
    sendSystem(next === "coding" ? "/code" : "/pm");
  };

  useEffect(() => {
    if (scrollRef.current !== null) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [timeline.length]);

  // When the user opens a session (sidebar click, deep link, tab
  // reopen), make sure a bridge pipeline is running. Without this,
  // navigating to an existing session would leave it stuck without a
  // sandbox until the user manually clicked Resume.
  useSessionBridgeEnsure(sessionId);

  // Tab close / reload → DETACH the session (`?reason=pagehide`) rather than
  // hard-cancel it: a reload keeps the bridge alive for the server's reconnect
  // grace so the reloaded tab re-attaches instead of cold-booting. A genuine
  // close that never re-attaches is reaped after the grace. Without any beacon
  // the 60-min idle sweep would otherwise hold the CI job for an hour. Explicit
  // sidebar "discard" stays a hard DELETE at its own call site.
  // Opts in for the same reason as RefineFlow: this route owns the socket, so
  // navigating away is a real departure and a return re-attaches.
  useSessionPagehideBeacon(sessionId, { detachOnUnmount: true });

  const onCreateMr = async () => {
    setMrError(null);
    setMrSubmitting(true);
    try {
      const res = await fetch(`/sessions/${sessionId}/coding/mr`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setMrError(text || t("chatTop.mrFailed", { status: res.status }));
        return;
      }
      const json = (await res.json()) as { url?: string };
      if (json.url && typeof json.url === "string") {
        window.open(json.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setMrError((err as Error).message);
    } finally {
      setMrSubmitting(false);
    }
  };

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Only send when the socket is actually OPEN. `send` is a silent
    // no-op off-OPEN and the server, not the client, echoes the user
    // message into the timeline — so sending on a dropped ("closed") WS
    // would clear the draft and lose the message with no trace. Preserve
    // the draft and let the user reconnect instead.
    if (draft.trim() === "" || status !== "open") return;
    sendComposer(draft);
  };

  // Phase 10a — proposal confirm/reject round-trips through a chat message
  // until phase 10a-streaming wires a dedicated WS control kind. The server
  // recognises the `/confirm <id>` and `/reject <id>` shapes and routes them
  // to the apply seam.
  const onProposalConfirm = (proposalId: string) => {
    send(`/confirm ${proposalId}`, turnFlow);
  };
  const onProposalReject = (proposalId: string) => {
    send(`/reject ${proposalId}`, turnFlow);
  };

  return (
    // Remount-safe store for the timeline subtree: every entry unmounts during
    // the reconnect replay window (and, embedded in IssuePage, on side-tab
    // switches), so widget drafts / selections / one-shot latches re-seed from
    // this session-scoped bag instead of resetting. With an externalSession
    // this is the owner's store — the same one IssuePage provides around its
    // pinned readiness card.
    <RemountSafeStoreProvider store={uiState}>
    <div className={embedded ? "flex h-full" : "flex h-full min-h-screen"}>
      {(idleCountdownEndsAt !== null || status === "idle_killed") && (
        <IdleReconnectDialog
          endsAt={idleCountdownEndsAt}
          killed={status === "idle_killed"}
          onStay={dismissIdle}
          onRestart={() => {
            // The killed session id is baked into this page's URL — a plain
            // reload would just re-attach the discarded session's replayed
            // timeline. Leave for the picker instead.
            window.location.assign("/");
          }}
        />
      )}
      {!sidebarHidden && (
        <HistorySidebar activeSessionId={sessionId} onPick={onPickSession} />
      )}
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-line bg-surface px-4 py-2">
          <div className="flex items-center gap-3">
            {/* The session id, SHORTENED. It is the trace id an operator needs
                (this surface has no URL of its own — the drawer adopts a
                session into React state), but the full 36-character UUID was
                rendered as ordinary body text at 14px next to the title. The
                whole of it is on the element for a copy. */}
            <div
              className="font-mono text-xs text-ink-faint"
              title={sessionId ?? ""}
            >
              {(sessionId ?? "").slice(0, 8)}
            </div>
            <BridgeBadge
              bridge={bridge}
              onReconnect={onReconnect}
              reconnecting={reconnecting}
            />
            <SandboxStatus
              sandbox={sandbox}
              onRetry={onReconnect}
              onRestart={onReconnect}
              pending={reconnecting}
            />
            <ModeToggle
              mode={mode}
              bridgeReady={bridge.state === "ready"}
              onSelect={onModeSelect}
            />
          </div>
          <div className="flex items-center gap-3">
            {status === "connecting" && <Spinner label={t("chatTop.connecting")} />}
            {status === "open" && <span className="text-xs text-success-fg">{t("chatTop.connected")}</span>}
            {status === "closed" && (
              <span className="flex items-center gap-1.5 text-xs text-caution-fg">
                {t("chatTop.disconnected")}
                <button
                  type="button"
                  onClick={reconnect}
                  className="rounded underline underline-offset-2 hover:text-caution-fg-3"
                >
                  {t("chatTop.reconnect")}
                </button>
              </span>
            )}
            {status === "expired" && <span className="text-xs text-danger-fg">{t("chatTop.sessionExpired")}</span>}
            {status === "denied" && <span className="text-xs text-danger-fg">{t("chatTop.accessDenied")}</span>}
            {isPureChat && (
              <button
                type="button"
                onClick={() => setNewChatArmed((armed) => !armed)}
                className={
                  newChatArmed
                    ? "rounded border border-primary-line bg-primary-soft px-2 py-1 text-xs font-medium text-primary-fg"
                    : "rounded border border-line-strong px-2 py-1 text-xs font-medium text-ink-3 hover:bg-surface-2"
                }
                title={t("chatTop.newChatTitle")}
                aria-pressed={newChatArmed}
                data-testid="new-chat-button"
              >
                {newChatArmed ? t("chatTop.newChatArmed") : t("chatTop.newChat")}
              </button>
            )}
            <button
              type="button"
              onClick={() => void onResetClick()}
              disabled={resetting}
              className="rounded border border-danger-line-strong px-2 py-1 text-xs font-medium text-danger-fg hover:bg-danger-soft disabled:opacity-50"
              title={t("chatTop.resetTitle")}
              data-testid="reset-session-button"
            >
              {resetting ? t("chatTop.resetting") : t("chatTop.reset")}
            </button>
            {!embedded && (
              <Button variant="ghost" onClick={onBackToPicker}>
                {t("chatTop.switchProject")}
              </Button>
            )}
          </div>
        </header>

        <div className="space-y-2 border-b border-line px-4 py-2">
          <ReauthBanner
            visible={reauth.required}
            gitlabHost={reauth.gitlabHost}
            onComplete={clearReauth}
          />
          {status === "expired" && (
            <Banner tone="error">{t("chatTop.sessionInactive")}</Banner>
          )}
          {status === "denied" && (
            <Banner tone="error">{t("chatTop.accessRevoked")}</Banner>
          )}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" aria-label={t("chatTop.conversationLabel")}>
          <div className="mx-auto flex max-w-2xl flex-col gap-3">
            {visibleTimeline.length === 0 && (
              <p className="text-center text-sm text-ink-subtle">
                {t("chatTop.emptyTimelinePrefix")}<code>/quality 123</code>.
              </p>
            )}
            {visibleTimeline.map((entry) => (
              <RenderEntry
                key={entry.id}
                entry={entry}
                onProposalConfirm={onProposalConfirm}
                onProposalReject={onProposalReject}
                onAcceptChanges={(text) => send(text, turnFlow)}
                cardHold={cardHold}
                {...(projectId !== undefined ? { projectId } : {})}
                {...(milestoneListProjectId !== undefined ? { milestoneListProjectId } : {})}
                {...(applyFailure !== undefined ? { applyFailure } : {})}
              />
            ))}
          </div>
        </div>

        {branch.commits > 0 && (
          <div
            data-testid="branch-state-footer"
            className="flex items-center justify-between gap-3 border-t border-caution-line bg-caution-soft px-4 py-2"
          >
            <div className="text-xs text-caution-fg-3">
              {tn("plural.commits", branch.commits)} {t("chatTop.commitsOn")}{" "}
              <code className="font-mono">loom-coding/{sessionId.slice(0, 8)}</code>
              {branch.summary !== null && (
                <span className="ml-2 text-caution-fg">· {branch.summary}</span>
              )}
              {mrError !== null && (
                <span className="ml-2 text-danger-fg">— {mrError}</span>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={onCreateMr}
              disabled={mrSubmitting || bridge.state !== "ready"}
            >
              {mrSubmitting ? t("chatTop.creatingMr") : t("chatTop.createMr")}
            </Button>
          </div>
        )}

        <form
          onSubmit={submit}
          className="flex items-end gap-2 border-t border-line bg-surface px-4 py-3"
        >
          <AutoGrowTextarea
            aria-label={t("chatTop.messageLabel")}
            placeholder={t("chatTop.messagePlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (draft.trim() !== "" && status === "open") {
                  sendComposer(draft);
                }
              }
            }}
            minLines={2}
            className={`flex-1 ${FIELD}`}
            disabled={isSessionDead(status)}
          />
          <Button
            type="submit"
            disabled={draft.trim() === "" || status !== "open"}
          >
            {t("common.send")}
          </Button>
        </form>
      </main>
    </div>
    </RemountSafeStoreProvider>
  );
}
